import { Pencil, Plus, X } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Callout, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  type StageCadenceRow, bodySummary, firstCallText, stageCadenceItems, stageCadenceRows, thenText,
} from "@/lib/cadence";
import { useCadences, useSetStageCadences, useStages } from "@/lib/queries";
import { RECOVERY_STAGE, STAGE_SECTIONS, sectionText, stageSection } from "@/lib/stages";
import type { Cadence, Campaign, StageRow } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The stage table on a campaign's Cadence tab (ADR-087): which stages run a cadence of their own.
 * A stage not in the table runs the campaign's default, picked on the Dialing tab; an exit stage
 * not in the table runs none, since the lead leaves the list. Giving an exit a cadence is what
 * keeps a signed client the firm cannot reach on the same campaign: MIA on the MIA cadence.
 *
 * The table is saved whole (`PUT /campaigns/{id}/stage-cadences`), so each Add, Edit and Remove
 * sends the set with one row changed: `stageCadenceItems` in `lib/cadence.ts` builds it. The
 * dialer moves a row to its stage's cadence at its next look; nothing here touches a row.
 */
export function CampaignStageCadences({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const stages = useStages(campaign.tenant_id);
  const cadences = useCadences();
  const [editing, setEditing] = React.useState<{ stage: StageRow; cadenceId: number | null } | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [removing, setRemoving] = React.useState<string | null>(null);
  const save = useSetStageCadences();
  const toast = useToast();

  const rows = React.useMemo(() => stageCadenceRows(stages.data ?? [], campaign), [stages.data, campaign]);
  const bodies = React.useMemo(
    () => new Map((cadences.data ?? []).map((c) => [c.id, c.current_version?.body ?? null])),
    [cadences.data],
  );
  const listed = campaign.stage_cadences ?? [];
  const order = (stages.data ?? []).map((s) => s.slug);
  // Of the end stages only MIA is offered a cadence: it is the one that is worked again. A cadence
  // on Signed or Not qualified would have the campaign calling leads who are done. One that
  // already has a cadence stays in the table, to be seen and removed.
  const free = rows
    .filter((r) => r.source !== "own" && r.source !== "missing")
    .map((r) => r.stage)
    .filter((s) => s.group !== "exit" || s.slug === RECOVERY_STAGE);

  const remove = async (row: StageCadenceRow) => {
    setRemoving(row.stage.slug);
    try {
      await save.mutateAsync({ id: campaign.id, items: stageCadenceItems(listed, row.stage.slug, null, order) });
      toast.success("Stage cadence removed", `${row.stage.label} runs the campaign's cadence again.`);
    } catch (err) {
      toast.error("Could not remove it", err instanceof ApiError ? err.message : undefined);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Stage cadences"
        description="A stage in this table runs its own cadence: when a lead's stage changes, their row starts that cadence from its first call. A stage not listed runs the campaign's cadence; an exit stage not listed runs none."
        action={
          canManage && (
            <Button variant="outline" size="sm" onClick={() => setAdding(true)} disabled={free.length === 0 || !cadences.data}>
              <Plus />
              Add a stage
            </Button>
          )
        }
      />
      {stages.isPending || cadences.isPending ? (
        <TableSkeleton cols={4} rows={5} />
      ) : stages.error ? (
        <div className="p-4"><Callout tone="danger">Could not load the stages.</Callout></div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-48">Stage</TableHead>
                <TableHead className="min-w-64">Cadence</TableHead>
                <TableHead className="whitespace-nowrap">First call</TableHead>
                <TableHead className="min-w-56">Then</TableHead>
                {canManage && <TableHead className="w-24" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {STAGE_SECTIONS.map((group) => {
                const inGroup = rows.filter((r) => stageSection(r.stage.slug, r.stage.group) === group);
                if (inGroup.length === 0) return null;
                return (
                  <React.Fragment key={group}>
                    <TableRow className="bg-surface-sunken/60 hover:bg-surface-sunken/60">
                      <TableCell colSpan={canManage ? 5 : 4} className="py-1.5 text-2xs font-medium uppercase tracking-wide text-text-subtle">
                        {sectionText(group).label}
                      </TableCell>
                    </TableRow>
                    {inGroup.map((row) => (
                      <StageLine
                        key={row.stage.slug}
                        row={row}
                        body={row.cadence ? bodies.get(row.cadence.id) : null}
                        canManage={canManage}
                        busy={removing === row.stage.slug}
                        onEdit={() => setEditing({ stage: row.stage, cadenceId: row.cadence?.id ?? null })}
                        onRemove={() => void remove(row)}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <StageCadenceDialog
        open={adding || editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAdding(false);
            setEditing(null);
          }
        }}
        campaign={campaign}
        stage={editing?.stage ?? null}
        cadenceId={editing?.cadenceId ?? null}
        stages={free}
        cadences={cadences.data ?? []}
        order={order}
      />
    </Card>
  );
}

function StageLine({
  row, body, canManage, busy, onEdit, onRemove,
}: {
  row: StageCadenceRow;
  body: Parameters<typeof firstCallText>[0];
  canManage: boolean;
  busy: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const own = row.source === "own";
  return (
    <TableRow>
      <TableCell>
        <span className="text-sm text-text">{row.stage.label}</span>
        {row.stage.description && <span className="block max-w-xs truncate text-xs text-text-muted">{row.stage.description}</span>}
      </TableCell>
      <TableCell>
        {row.source === "missing" ? (
          <Badge tone="danger" size="sm">Cadence gone</Badge>
        ) : row.cadence ? (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Link to={`/cadences/${row.cadence.id}`} className={cn("text-sm hover:underline", own ? "font-medium text-text" : "text-text")}>
              {row.cadence.name}
            </Link>
            {row.source === "default" && <Badge tone="outline" size="sm" title="The campaign's cadence, from the Dialing tab">default</Badge>}
            {row.cadence.problem && <Badge tone="danger" size="sm">{row.cadence.problem}</Badge>}
          </span>
        ) : (
          <span className="text-sm text-text-muted">No calls</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-text-muted">
        {row.cadence ? firstCallText(body) : row.source === "none" ? "the lead leaves the list" : "—"}
      </TableCell>
      <TableCell className="text-xs text-text-muted">{row.cadence ? thenText(body) : "—"}</TableCell>
      {canManage && (
        <TableCell className="text-right">
          {own && (
            <span className="inline-flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={onEdit} title="Change the cadence" disabled={busy}>
                <Pencil />
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={onRemove} title="Back to the campaign's cadence" loading={busy}>
                <X />
                Remove
              </Button>
            </span>
          )}
          {row.source === "missing" && (
            <Button variant="ghost" size="sm" onClick={onEdit} title="Pick a cadence that exists">
              <Pencil />
              Fix
            </Button>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

function StageCadenceDialog({
  open, onOpenChange, campaign, stage, cadenceId, stages, cadences, order,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
  /** The stage being edited; null to add one. */
  stage: StageRow | null;
  cadenceId: number | null;
  /** The stages that can still be added. */
  stages: StageRow[];
  cadences: Cadence[];
  order: string[];
}) {
  const save = useSetStageCadences();
  const toast = useToast();
  const [pickedStage, setPickedStage] = React.useState<string>("");
  const [pickedCadence, setPickedCadence] = React.useState<string>("");
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setPickedStage(stage?.slug ?? stages[0]?.slug ?? "");
    setPickedCadence(cadenceId !== null ? String(cadenceId) : "");
    setFormError(null);
  }, [open, stage, cadenceId, stages]);

  // A retired cadence is offered only while the stage still names it, to be seen and replaced.
  const offered = cadences.filter((c) => c.active || c.id === cadenceId);
  const chosenStage = stage ?? stages.find((s) => s.slug === pickedStage) ?? null;
  const chosenCadence = offered.find((c) => String(c.id) === pickedCadence) ?? null;
  const isDefault = chosenCadence !== null && chosenCadence.id === campaign.cadence_id;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!chosenStage || !chosenCadence) {
      setFormError("Pick a stage and a cadence.");
      return;
    }
    try {
      await save.mutateAsync({
        id: campaign.id,
        items: stageCadenceItems(campaign.stage_cadences ?? [], chosenStage.slug, chosenCadence.id, order),
      });
      toast.success(stage ? "Stage cadence changed" : "Stage cadence set", `${chosenStage.label} runs ${chosenCadence.name}.`);
      onOpenChange(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not save the stage's cadence.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{stage ? `${stage.label}: change the cadence` : "Give a stage its own cadence"}</DialogTitle>
          <DialogDescription>
            When a lead's stage changes to this one, their row starts the cadence from its first call. A lead already in the
            stage moves to it at the dialer's next look. A promised callback keeps its time.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={(e) => void submit(e)} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field label="Stage" htmlFor="stage-cadence-stage" hint={chosenStage?.description || (chosenStage ? sectionText(stageSection(chosenStage.slug, chosenStage.group)).description : undefined)}>
              {stage ? (
                <p id="stage-cadence-stage" className="text-sm text-text">{stage.label}</p>
              ) : (
                <Select value={pickedStage} onValueChange={setPickedStage}>
                  <SelectTrigger id="stage-cadence-stage">
                    <SelectValue placeholder="Pick a stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map((s) => (
                      <SelectItem key={s.slug} value={s.slug} description={sectionText(stageSection(s.slug, s.group)).label}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field
              label="Cadence"
              htmlFor="stage-cadence-cadence"
              hint={
                isDefault
                  ? "This is the campaign's cadence already: a stage not listed runs it."
                  : chosenCadence?.current_version
                    ? bodySummary(chosenCadence.current_version.body)
                    : undefined
              }
              action={
                <Link to="/cadences" className="text-xs text-text-muted hover:text-text hover:underline">
                  All cadences
                </Link>
              }
            >
              <Select value={pickedCadence} onValueChange={setPickedCadence}>
                <SelectTrigger id="stage-cadence-cadence">
                  <SelectValue placeholder="Pick a cadence" />
                </SelectTrigger>
                <SelectContent>
                  {offered.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={String(c.id)}
                      description={c.current_version ? bodySummary(c.current_version.body) : "No version to run yet"}
                    >
                      {c.name}
                      {!c.active && " (retired)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" loading={save.isPending} disabled={!chosenStage || !chosenCadence}>
              {stage ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
